import { useCallback, useMemo } from 'react';
import { Badge, DropDown, Box, toast } from 'dash-ui';
import { useTranslation } from 'react-i18next';
import {
  GetPropertiesDocument,
  useSetPropertyStatusMutation,
  PropertyStatusesEnum,
  GetPropertyByIdDocument,
} from 'gql/generated.graphql';

import { IBadgePropertyStatusChange } from './BadgePropertyStatusChange.interfaces';
import { BADGE_TO_STATUS_COLOR_MAP } from './BadgePropertyStatusChange.constants';
import { getIsMarketOptional } from 'dash-common';

function BadgePropertyStatusChange({
  currentStatus,
  propertyId,
  primaryOrganizationId,
  marketArea,
  location,
}: IBadgePropertyStatusChange) {
  const { t } = useTranslation('common', { keyPrefix: 'labels' });
  const { t: tPages } = useTranslation('pages');
  const [setPropertyStatusMutation, { loading }] = useSetPropertyStatusMutation(
    {
      refetchQueries: [GetPropertiesDocument, GetPropertyByIdDocument],
    }
  );

  // FIXME: re-creating context functions everytime state change. Use useCallback
  const onChangeStatus = useCallback(
    (newStatus: PropertyStatusesEnum) => {
      if (!primaryOrganizationId) {
        toast.error(tPages('propertyPage.noPrimaryOrganizationError'));
        return;
      }
      if (!getIsMarketOptional(location.address.state) && !marketArea) {
        toast.error(tPages('propertyPage.noMarketAreaError'));
        return;
      }
      if (!loading) {
        setPropertyStatusMutation({
          variables: { input: { status: newStatus, propertyId } },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, propertyId, primaryOrganizationId, marketArea]
  );

  const statusOptions = useMemo(
    () => [
      {
        key: PropertyStatusesEnum.Private,
        children: t`private`,
        onClick: () => onChangeStatus(PropertyStatusesEnum.Private),
      },
      {
        key: PropertyStatusesEnum.Public,
        children: t`public`,
        onClick: () => onChangeStatus(PropertyStatusesEnum.Public),
      },
      {
        key: PropertyStatusesEnum.Draft,
        children: t`draft`,
        onClick: () => onChangeStatus(PropertyStatusesEnum.Draft),
      },
    ],
    [t, onChangeStatus]
  );

  return (
    <Box w={114}>
      <DropDown
        w={114}
        Anchor={({ isOpen }) => (
          <Badge
            h={32}
            square
            loading={loading}
            type={BADGE_TO_STATUS_COLOR_MAP[currentStatus]}
            iconRight={isOpen ? 'arrowUp' : 'arrowDown'}
            title={t(currentStatus.toLowerCase())}
          />
        )}
        items={statusOptions}
      />
    </Box>
  );
}

export default BadgePropertyStatusChange;
